import React, { createContext, useState, useEffect } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          const response = await authService.getProfile();
          if (response.success) {
            setUser(response.data);
            setIsAuthenticated(true);
          }
        } catch (error) {
          authService.logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      return { 
        success: true, 
        data: response, 
        requires_verification: response.requires_verification, 
        email: response.email 
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      // Gérer le cas MFA requis
      if (response.mfa_required) {
        return { success: true, mfa_required: true, userId: response.userId };
      }
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, data: response.data };
      }
      return { success: false, error: response.message };
    } catch (error) {
      if (error.data && error.data.requires_verification) {
        return { 
          success: false, 
          requires_verification: true, 
          email: error.data.email, 
          error: error.message 
        };
      }
      return { success: false, error: error.message };
    }
  };

  const verify2FA = async (userId, token) => {
    try {
      const response = await authService.verify2FA(userId, token);
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, data: response.data };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };


  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      if (response.success) {
        const updatedUser = await authService.getProfile();
        setUser(updatedUser.data);
      }
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const submitKYC = async (kycData) => {
    try {
      const response = await authService.submitKYC(kycData);
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateTelegram = async (telegramUsername) => {
    try {
      const response = await authService.updateTelegram(telegramUsername);
      if (response.success) {
        // Recharger le profil pour mettre à jour l'état local
        const updatedUser = await authService.getProfile();
        setUser(updatedUser.data);
      }
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    register,
    login,
    verify2FA,
    logout,

    updateProfile,
    submitKYC,
    updateTelegram,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };